import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AppProvider } from '@/contexts/AppContext';
import { LoginPage } from '@/components/auth/LoginPage';
import { ContratosPage } from '@/pages/ContratosPage';
import { ContratoDetailPage } from '@/pages/ContratoDetailPage';
import { CatalogoPage } from '@/pages/CatalogoPage';
import { CapturePage } from '@/pages/CapturePage';
import { SabanaPage } from '@/pages/SabanaPage';
import { GeneradoresPage } from '@/pages/GeneradoresPage';
import { PresupuestoPage } from '@/pages/PresupuestoPage';
import { OportunidadesPage } from '@/pages/OportunidadesPage';
import LandingApp from '@/pages/LandingApp';
import LandingServicio from '@/pages/LandingServicio';
import { AdminDashboardPage } from '@/pages/AdminDashboardPage';
import { Toaster } from '@/components/ui/sonner';
import { OfflineIndicator } from '@/components/layout/OfflineIndicator';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1B3A6B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin-only route: redirects non-super_admin users to /contratos
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1B3A6B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only super_admin can access admin routes
  if (user?.role !== 'super_admin') {
    return <Navigate to="/contratos" replace />;
  }

  return children;
};

// Route that redirects admin to admin dashboard
const HomeRedirect = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/contratos" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    // Initialize demo data on first load
    const initDemoData = async () => {
      try {
        await axios.post(`${API}/init-demo-data`);
        console.log('Demo data initialized');
      } catch (error) {
        console.log('Demo data already exists or error:', error);
      }
    };

    initDemoData();
  }, []);

  return (
    <Routes>
      {/* Login - redirect based on role */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            isAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/contratos" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      
      {/* Admin Dashboard - super_admin only */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboardPage />
          </AdminProtectedRoute>
        }
      />
      
      {/* Screen 1: Mis Contratos (for non-admin users) */}
      <Route
        path="/contratos"
        element={
          <ProtectedRoute>
            <ContratosPage />
          </ProtectedRoute>
        }
      />
      
      {/* Screen 2: Contrato Detail (with 3 buttons) */}
      <Route
        path="/contrato"
        element={
          <ProtectedRoute>
            <ContratoDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* Screen 3: Catálogo de Conceptos */}
      <Route
        path="/catalogo"
        element={
          <ProtectedRoute>
            <CatalogoPage />
          </ProtectedRoute>
        }
      />
      
      {/* Screen 4: Capture */}
      <Route
        path="/capture"
        element={
          <ProtectedRoute>
            <CapturePage />
          </ProtectedRoute>
        }
      />
      
      {/* Sábana */}
      <Route
        path="/sabana"
        element={
          <ProtectedRoute>
            <SabanaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/presupuesto"
        element={
          <AdminProtectedRoute>
            <PresupuestoPage />
          </AdminProtectedRoute>
        }
      />
      
      {/* Generadores */}
      <Route
        path="/generadores"
        element={
          <ProtectedRoute>
            <GeneradoresPage />
          </ProtectedRoute>
        }
      />
      
      {/* Oportunidades - Marketplace (PUBLIC - no login required) */}
      <Route
        path="/oportunidades"
        element={<OportunidadesPage />}
      />
      
      {/* Landing Servicio - Main public page (PUBLIC - no login required) */}
      <Route
        path="/"
        element={<LandingServicio />}
      />
      
      {/* Landing App - Original app landing page */}
      <Route
        path="/app"
        element={<LandingApp />}
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <OfflineIndicator />
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
